import { List, useTable } from "@refinedev/antd";
import { Input, Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";

import { DATE_FORMAT, SCHOOL_GRADES, planLabel } from "../labels";
import type { StudentListItem } from "../types";

const PLAN_OPTIONS = [
  { value: "", label: "Tümü" },
  { value: "active", label: "Aktif planı var" },
  { value: "none", label: "Planı yok" },
];

function filterValue(filters: ReturnType<typeof useTable>["filters"], field: string) {
  const filter = filters.find((item) => "field" in item && item.field === field);
  return (filter?.value as string | undefined) ?? "";
}

/** /students: students, newest first (permission `students.view`). */
export function StudentsPage() {
  const navigate = useNavigate();
  const { tableProps, filters, setFilters } = useTable<StudentListItem>({
    resource: "students",
    pagination: { mode: "server", pageSize: 25 },
    syncWithLocation: true,
  });
  const plan = filterValue(filters, "plan");
  const search = filterValue(filters, "search");
  const setFilter = (field: "plan" | "search", value: string) =>
    setFilters(
      [
        { field: "plan", operator: "eq", value: field === "plan" ? value : plan },
        { field: "search", operator: "eq", value: field === "search" ? value : search },
      ],
      "replace",
    );

  return (
    <List title="Öğrenciler">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={PLAN_OPTIONS}
          value={plan}
          onChange={(value) => setFilter("plan", String(value))}
        />
        <Input.Search
          allowClear
          placeholder="Ad, soyad veya e-posta"
          defaultValue={search}
          onSearch={(value) => setFilter("search", value)}
          style={{ width: 260 }}
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        // The whole row opens the card, not only the name.
        onRow={(student) => ({
          onClick: () => navigate(`/students/${student.id}`),
          style: { cursor: "pointer" },
        })}
      >
        <Table.Column<StudentListItem>
          title="Öğrenci"
          dataIndex="name"
          render={(name: string, student) => (
            <Space direction="vertical" size={0}>
              <Link to={`/students/${student.id}`}>
                <Typography.Text strong>{name}</Typography.Text>
              </Link>
              <Typography.Text type="secondary">{student.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<StudentListItem>
          title="Sınıf"
          dataIndex="schoolGrade"
          render={(value: string) => SCHOOL_GRADES[value] ?? value}
        />
        <Table.Column<StudentListItem>
          title="Plan"
          dataIndex="plan"
          render={(value: StudentListItem["plan"]) =>
            value ? <Tag color="green">{planLabel(value)}</Tag> : <Tag>Plan yok</Tag>
          }
        />
        <Table.Column<StudentListItem> title="Ders" dataIndex="lessonCount" />
        <Table.Column<StudentListItem>
          title="Kayıt"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_FORMAT)}
        />
      </Table>
    </List>
  );
}
